/**
 * Bereitet ein Bild vor dem Hochladen auf.
 *
 * Der Anlass: ein Foto aus einer Kamera ist 4000 px breit und 6 MB gross. In
 * einer 600 px breiten Nachricht werden davon 600 px angezeigt — der Rest wird
 * mitgeladen und weggerechnet. Bei 2.000 Empfängern fällt das 2.000-mal an.
 *
 * Warum im Browser und nicht auf dem Server: die üblichen Bildbibliotheken für
 * Node (sharp, canvas) kompilieren nativen Code. Das Werkzeug soll sich auf dem
 * Kanzleiserver mit einem `npm install` einrichten lassen. Der Browser bringt
 * mit Canvas alles Nötige mit, und der Upload wird gleich mit kleiner.
 *
 * WICHTIG — was hier NICHT passiert: keine Umwandlung nach WebP. Das wäre die
 * naheliegende Wahl fürs Web und im Postfach ein Fehler: Outlook für Windows
 * rendert über die Word-Engine und stellt WebP gar nicht dar. Gmail wandelt es
 * vor der Zustellung nach JPEG um und verliert dabei die Transparenz. Für
 * E-Mail bleibt es bei JPG, PNG und GIF.
 */

/** Breite, auf die verkleinert wird. Doppelte Nachrichtenbreite für scharfe Darstellung auf hochauflösenden Bildschirmen. */
export const MAX_BREITE = 1200;

/** Qualität der JPEG-Kodierung. Darunter werden Flächen sichtbar fleckig. */
const JPEG_QUALITAET = 0.82;

/**
 * @typedef {object} Aufbereitet
 * @property {Blob} blob
 * @property {string} dateiname   Ggf. mit angepasster Endung.
 * @property {number} breite
 * @property {number} hoehe
 * @property {number} vorherBytes
 * @property {number} nachherBytes
 * @property {boolean} veraendert
 */

/**
 * @param {File} datei
 * @returns {Promise<Aufbereitet>}
 */
export async function bildVorbereiten(datei) {
  /** @type {Aufbereitet} */
  const unveraendert = {
    blob: datei,
    dateiname: datei.name,
    breite: 0,
    hoehe: 0,
    vorherBytes: datei.size,
    nachherBytes: datei.size,
    veraendert: false,
  };

  // Ein animiertes GIF überlebt den Weg durch Canvas nicht — es käme als
  // Standbild des ersten Einzelbildes zurück. Lieber unangetastet lassen.
  if (datei.type === 'image/gif') return unveraendert;

  let bild;
  try {
    bild = await createImageBitmap(datei);
  } catch {
    // Nicht lesbar: unverändert weiterreichen. Die Prüfung auf dem Server
    // entscheidet dann, ob es überhaupt ein Bild ist.
    return unveraendert;
  }

  try {
    const faktor = Math.min(1, MAX_BREITE / bild.width);
    const breite = Math.round(bild.width * faktor);
    const hoehe = Math.round(bild.height * faktor);

    const leinwand = document.createElement('canvas');
    leinwand.width = breite;
    leinwand.height = hoehe;
    const stift = leinwand.getContext('2d');
    if (!stift) return unveraendert;

    stift.imageSmoothingEnabled = true;
    stift.imageSmoothingQuality = 'high';
    stift.drawImage(bild, 0, 0, breite, hoehe);

    // PNG nur behalten, wo es gebraucht wird. Ein Foto als PNG ist um ein
    // Vielfaches grösser als dasselbe Foto als JPG — ohne sichtbaren Gewinn.
    // Bei Transparenz führt der Wechsel dagegen zu schwarzen Rändern.
    const braucht = hatTransparenz(stift, breite, hoehe);
    const typ = braucht ? 'image/png' : 'image/jpeg';

    const blob = await new Promise((aufloesen) =>
      leinwand.toBlob((ergebnis) => aufloesen(ergebnis), typ, JPEG_QUALITAET),
    );
    if (!blob) return unveraendert;

    // Wenn das Ergebnis nicht kleiner ist, hat die Umwandlung nichts gebracht.
    // Dann bleibt das Original — es ist mindestens so gut und unverfälscht.
    //
    // Die Bedingung gilt bewusst UNABHÄNGIG davon, ob verkleinert wurde: ein
    // sparsam kodiertes PNG mit wenigen Farben wird von Canvas als 32-Bit-RGBA
    // neu geschrieben und kann dabei selbst nach dem Verkleinern größer
    // ausfallen als das Original. Es dann hochzuladen wäre das Gegenteil des
    // Zwecks — und könnte die Datei über die Größengrenze des Servers heben.
    if (blob.size >= datei.size) return unveraendert;

    return {
      blob,
      dateiname: endungAnpassen(datei.name, braucht ? '.png' : '.jpg'),
      breite,
      hoehe,
      vorherBytes: datei.size,
      nachherBytes: blob.size,
      veraendert: true,
    };
  } finally {
    bild.close?.();
  }
}

/**
 * Prüft, ob im Bild überhaupt durchscheinende Pixel vorkommen.
 *
 * Nicht jedes PNG braucht PNG zu bleiben: die meisten sind schlicht als PNG
 * exportiert worden. Nur wenn wirklich Transparenz vorhanden ist, würde der
 * Wechsel zu JPG sichtbaren Schaden anrichten.
 *
 * @param {CanvasRenderingContext2D} stift
 * @param {number} breite
 * @param {number} hoehe
 * @returns {boolean}
 */
function hatTransparenz(stift, breite, hoehe) {
  let daten;
  try {
    daten = stift.getImageData(0, 0, breite, hoehe).data;
  } catch {
    // Bei fremdem Bildmaterial kann die Leinwand gesperrt sein. Im Zweifel
    // PNG behalten: eine grössere Datei ist besser als ein schwarzer Rand.
    return true;
  }

  // Jeder vierte Wert ist die Deckkraft. Bei grossen Bildern reicht eine
  // Stichprobe — ein Logo mit Freistellung hat tausende transparente Pixel,
  // eines davon zu treffen ist praktisch sicher.
  const schritt = daten.length > 4_000_000 ? 4 * 7 : 4;
  for (let i = 3; i < daten.length; i += schritt) {
    if ((daten[i] ?? 255) < 250) return true;
  }
  return false;
}

/**
 * @param {string} name
 * @param {string} neueEndung
 * @returns {string}
 */
function endungAnpassen(name, neueEndung) {
  const punkt = name.lastIndexOf('.');
  const stamm = punkt > 0 ? name.slice(0, punkt) : name;
  return `${stamm}${neueEndung}`;
}

/**
 * @param {number} bytes
 * @returns {string}
 */
export function bytesFormatieren(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
